import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, Dialog, H1, H2, ListRow, Separator, Text } from '@betterment/samba-react';

import { currentYear } from '../../../FourOhOneKDeferralSettings/models';
import { EmployersContributionConfigQuery } from '../../graphql/query.employersContributionConfig.generated';
import { ProjectionResponseData } from '../../types';
import { toFormattedMoney } from '../../utils';

type CurrentYearContributions = NonNullable<EmployersContributionConfigQuery['fourOhOneKContributions']>['thisYear'];

interface Props {
  projections: ProjectionResponseData;
  currentYearContributions: CurrentYearContributions;
}

export const DeferralProjectionDetailsDialog: React.FC<Props> = ({ projections, currentYearContributions }) => {
  const getLineItemAmount = (lineItemName: string) => {
    return currentYearContributions.result.lineItems.find(item => item.name === lineItemName)?.amount || toMoney(0);
  };

  const traditional401kYtdAmount = getLineItemAmount('Traditional 401(k)');
  const roth401kYtdAmount = getLineItemAmount('Roth 401(k)');
  const external401kYtdAmount = getLineItemAmount('External 401(k)');

  return (
    <Dialog.Content css={{ minWidth: '640px', p: 'space.400' }}>
      <Dialog.Title asChild={true}>
        <H1 variant="title300" css={{ pb: 'space.200' }}>
          Your {currentYear()} contributions
        </H1>
      </Dialog.Title>
      <Separator css={{ mb: 'space.0' }} />
      {/* Traditional 401K */}
      <Dialog.Description>
        <H2 variant="title100" css={{ pt: 'space.300' }}>
          Traditional 401(k)
        </H2>
        <ListRow css={{ pb: 'space.400', pt: 'space.100', pl: 'space.0', pr: 'space.0' }}>
          <ListRow.LeadingContent>
            <Text>Year-to-date</Text>
            <Text>Projected</Text>
          </ListRow.LeadingContent>

          <ListRow.TrailingContent>
            <Text>{formatMoney(traditional401kYtdAmount)}</Text>
            <Text>{toFormattedMoney(Math.max(projections?.futureTraditionalContributionAmountCents || 0, 0))}</Text>
          </ListRow.TrailingContent>
        </ListRow>

        {/* Roth 401(k) Contributions */}

        <H2 variant="title100">Roth 401(k)</H2>
        <ListRow css={{ pb: 'space.400', pt: 'space.100', pl: 'space.0', pr: 'space.0' }}>
          <ListRow.LeadingContent>
            <Text>Year-to-date</Text>
            <Text>Projected</Text>
          </ListRow.LeadingContent>

          <ListRow.TrailingContent>
            <Text>{formatMoney(roth401kYtdAmount)}</Text>
            <Text>{toFormattedMoney(Math.max(projections?.futureRothContributionAmountCents || 0, 0))}</Text>
          </ListRow.TrailingContent>
        </ListRow>
        {/* Outside 401(k) Contributions */}
        <H2 variant="title100" css={{ pt: 'space.100' }}>
          Outside 401(k)
        </H2>
        <ListRow css={{ pb: 'space.0', pt: 'space.100', pl: 'space.0', pr: 'space.0' }}>
          <ListRow.LeadingContent>
            <Text>Year-to-date</Text>
          </ListRow.LeadingContent>

          <ListRow.TrailingContent>
            <Text>{formatMoney(external401kYtdAmount)}</Text>
          </ListRow.TrailingContent>
        </ListRow>
        {/* Guidance */}
        <Text
          as="p"
          color="text"
          css={{
            fontWeight: 'medium',
            marginBottom: 'space.0',
            py: 'space.300',
            _last: { marginBottom: 'space.0' },
          }}
        >
          You can still contribute{' '}
          <span className="u-color-blue">{formatMoney(currentYearContributions?.result.amountRemainingToLimit)}</span>{' '}
          to max out your 401(k) for {currentYear()}.
        </Text>

        {/* Footer notes */}
        <Box>
          <Text as="p" color="text.weak" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.20' } }}>
            *These calculations show how much you will save for this calendar year, including existing contributions and
            estimated contributions for both Roth and Traditional accounts.
          </Text>
          <Text as="p" color="text.weak" css={{ marginBottom: 'space.0', _last: { marginBottom: 'space.0' } }}>
            We use salary, pay frequency, employee contributions, and contribution rate to calculate the projection.
          </Text>
        </Box>
      </Dialog.Description>
    </Dialog.Content>
  );
};
